import { createFileRoute, notFound, Outlet, redirect } from "@tanstack/react-router";
import { useEffect } from "react";
import { setLanguage } from "@/i18n";
import { DEFAULT_LANG, isLang, type Lang } from "@/i18n/config";
import { localeReadinessQuery } from "@/lib/content-queries";


/** Public slugs that used to live without a language prefix. */
const LEGACY_SLUGS = new Set([
  "nieuwbouw",
  "renovatie",
  "technieken",
  "warmtepompen-afdekkingen",
  "realisaties",
  "opbouw-zwembad",
  "over-ons",
  "offerte",
  "blog",
]);

export const Route = createFileRoute("/$lang")({
  staticData: { sitemap: false },
  beforeLoad: ({ params, location }) => {
    if (isLang(params.lang)) {
      // Buiten de React-render, zodat server en client dezelfde taal gebruiken.
      setLanguage(params.lang);
      return;
    }
    if (LEGACY_SLUGS.has(params.lang)) {
      throw redirect({
        href: `/${DEFAULT_LANG}${location.pathname}${location.searchStr ?? ""}`,
        statusCode: 302,
      });
    }
    throw notFound();
  },
  loader: async ({ context, params }) => {
    if (!isLang(params.lang) || params.lang === DEFAULT_LANG) return { indexeerbaar: true };
    try {
      const readiness = await context.queryClient.ensureQueryData(localeReadinessQuery);
      return { indexeerbaar: readiness.find((rij) => rij.lang === params.lang)?.volledig ?? false };
    } catch {
      return { indexeerbaar: false };
    }
  },
  head: ({ params, loaderData }) => {
    const lang = isLang(params.lang) ? params.lang : DEFAULT_LANG;
    // Geen handmatige schakelaar: een taal blijft buiten de index tot elke pagina erin gepubliceerd is.
    const indexeerbaar = lang === DEFAULT_LANG || (loaderData?.indexeerbaar ?? false);
    return indexeerbaar ? {} : { meta: [{ name: "robots", content: "noindex, nofollow" }] };
  },

  component: LanguageLayout,
});

function LanguageLayout() {
  const { lang } = Route.useParams() as { lang: Lang };
  useEffect(() => {
    setLanguage(lang);
    document.documentElement.lang = lang;
  }, [lang]);
  return <Outlet />;
}
